import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { TrendingUp, Calendar, MapPin, Clock, FileSpreadsheet, FileText, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';

const PERIOD_OPTIONS = [
  { value: 1, label: '1 Lună' },
  { value: 3, label: '3 Luni' },
  { value: 6, label: '6 Luni' },
  { value: 12, label: '12 Luni' },
];

const ReportsPage = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [locationsData, setLocationsData] = useState([]);
  const [hoursData, setHoursData] = useState([]);
  const [retentionData, setRetentionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [months, setMonths] = useState(6);

  const fetchData = useCallback(async (m) => {
    setLoading(true);
    try {
      const [revenue, attendance, locations, hours, retention] = await Promise.all([
        reportsAPI.getRevenue(m),
        reportsAPI.getAttendance(m),
        reportsAPI.getLocations(),
        reportsAPI.getHours(),
        reportsAPI.getRetention(m),
      ]);
      setRevenueData(revenue.data || []);
      setAttendanceData(attendance.data || []);
      setLocationsData(locations.data || []);
      setHoursData((hours.data || []).filter(h => h.count > 0));
      setRetentionData(retention.data || []);
    } catch (error) {
      toast.error('Eroare la sincronizarea statisticilor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(months); }, [fetchData, months]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const resp = format === 'xlsx' ? await reportsAPI.exportExcel({}) : await reportsAPI.exportPdf({});
      const blob = new Blob([resp.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Raport_Nautica.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Export ${format.toUpperCase()} descărcat!`);
    } catch { toast.error(`Eroare la export`); }
    finally { setExporting(false); }
  };

  const totalRevenue = revenueData.reduce((sum, d) => sum + (d.revenue || 0), 0);
  const totalAttendance = attendanceData.reduce((sum, d) => sum + (d.attendance || 0), 0);
  const avgChurn = retentionData.length > 0
    ? (retentionData.reduce((s, d) => s + (d.churn_rate || 0), 0) / retentionData.length).toFixed(1) : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#050505] border border-white/10 p-4 shadow-2xl">
          <p className="text-white text-xs font-bold uppercase tracking-widest mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-[#6db025] font-black text-lg">
              {p.value}{p.dataKey === 'revenue' ? ' LEI' : p.dataKey === 'churn_rate' ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-[#6db025] font-heading tracking-widest uppercase">Generare Rapoarte...</div></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Performanță Club</h1>
          <p className="text-white/40 text-sm mt-1">Statistici financiare și operaționale - Ultimele {PERIOD_OPTIONS.find(o => o.value === months)?.label}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-2">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setMonths(opt.value)}
                className={`px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${months === opt.value ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button onClick={() => handleExport('xlsx')} disabled={exporting} className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider hover:bg-emerald-500/20 transition-colors disabled:opacity-40 rounded">
            <FileSpreadsheet size={14} /> XLSX
          </button>
          <button onClick={() => handleExport('pdf')} disabled={exporting} className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider hover:bg-red-500/20 transition-colors disabled:opacity-40 rounded">
            <FileText size={14} /> PDF
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { Icon: TrendingUp, label: 'Încasări', value: <>{totalRevenue.toLocaleString()} <span className="text-[#6db025] text-lg">LEI</span></> },
          { Icon: Calendar,   label: 'Intrări Total', value: totalAttendance.toLocaleString() },
          { Icon: Users,      label: 'Churn Mediu', value: <>{avgChurn}<span className="text-[#6db025] text-lg">%</span></> },
          { Icon: Clock,      label: 'Oră Aglomerată', value: hoursData.length > 0 ? `${hoursData.reduce((max, h) => h.count > max.count ? h : max).hour}:00` : 'N/A' },
        ].map(({ Icon, label, value }, i) => (
          <div key={i} className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden group">
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#6db025]" />
            <div className="p-6 pl-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#6db025]/10 flex items-center justify-center group-hover:bg-[#6db025] group-hover:text-black text-[#6db025] transition-colors rounded"><Icon size={20} /></div>
                <span className="text-white/40 text-[10px] font-black uppercase tracking-widest">{label}</span>
              </div>
              <p className="font-heading text-3xl font-bold text-white tracking-tight">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue + Attendance */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg mb-6">Evoluție Venituri</h3>
          <div className="h-64">
            {revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                  <XAxis dataKey="month" stroke="#666" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={(v) => v.split(' ')[0].substring(0, 3)} axisLine={false} tickLine={false} />
                  <YAxis stroke="#666" tick={{ fill: '#666', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{fill: '#ffffff05'}} />
                  <Bar dataKey="revenue" fill="#6db025" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-white/20 text-xs font-bold uppercase tracking-widest">Date Insuficiente</div>}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg mb-6">Trafic Prezențe</h3>
          <div className="h-64">
            {attendanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={attendanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                  <XAxis dataKey="month" stroke="#666" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={(v) => v.split(' ')[0].substring(0, 3)} axisLine={false} tickLine={false} />
                  <YAxis stroke="#666" tick={{ fill: '#666', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="attendance" stroke="#6db025" strokeWidth={3} dot={{ fill: '#050505', stroke: '#6db025', strokeWidth: 2, r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-white/20 text-xs font-bold uppercase tracking-widest">Date Insuficiente</div>}
          </div>
        </div>
      </div>

      {/* Retention / Churn Chart */}
      <div className="bg-[#0A0A0A] border border-white/5 p-6">
        <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg mb-1">Rata de Pierdere Clienți (Churn Rate)</h3>
        <p className="text-white/40 text-xs mb-6">Procentul clienților activi luna trecută care nu au reînnoit</p>
        <div className="h-64">
          {retentionData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={retentionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="month" stroke="#666" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={(v) => v.split(' ')[0].substring(0, 3)} axisLine={false} tickLine={false} />
                <YAxis stroke="#666" tick={{ fill: '#666', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="churn_rate" stroke="#FF3B30" fill="#FF3B30" fillOpacity={0.1} strokeWidth={2} dot={{ fill: '#050505', stroke: '#FF3B30', strokeWidth: 2, r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-white/20 text-xs font-bold uppercase tracking-widest">Date Insuficiente</div>}
        </div>
        {retentionData.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-4 pt-4 border-t border-white/5">
            {retentionData.map((d, i) => (
              <div key={i} className="text-center">
                <p className="text-white/30 text-[10px] uppercase font-bold">{d.month?.split(' ')[0]?.substring(0, 3)}</p>
                <p className={`font-heading text-lg font-bold ${d.churn_rate > 30 ? 'text-red-400' : d.churn_rate > 15 ? 'text-yellow-400' : 'text-emerald-400'}`}>{d.churn_rate}%</p>
                <p className="text-white/20 text-[10px]">{d.churned} pierduți</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location + Hours */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg mb-6">Performanță Locații</h3>
          {locationsData.length > 0 ? (
            <div className="space-y-4">
              {locationsData.map((loc, i) => (
                <div key={i} className="flex items-center gap-4 group">
                  <span className="text-[#6db025] font-heading font-black opacity-40 group-hover:opacity-100 transition-opacity">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-white text-xs font-bold uppercase tracking-wide">{loc.location}</span>
                      <span className="text-white/60 text-xs font-medium">{loc.count} intrări</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-[#6db025]" style={{ width: `${(loc.count / (locationsData[0]?.count || 1)) * 100}%` }} /></div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="h-32 flex items-center justify-center text-white/20 text-xs font-bold uppercase tracking-widest">Date Insuficiente</div>}
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 p-6">
          <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg mb-6">Distribuție Orară</h3>
          <div className="h-48 mt-4">
            {hoursData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hoursData}>
                  <XAxis dataKey="hour" stroke="#666" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={(v) => `${v}:00`} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{fill: '#ffffff05'}} />
                  <Bar dataKey="count" fill="#6db025" radius={[2, 2, 0, 0]} opacity={0.8} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-white/20 text-xs font-bold uppercase tracking-widest">Date Insuficiente</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;